/**
 * Pay tab — the dedicated payment surface. Surface modes (in order):
 *
 *   1. Cart with requires_review → WG-controlled review (auto-skips when
 *                                  the visitor's trip profile is complete
 *                                  so the partner iframe loads pre-filled).
 *   2. Cart with embedded_url    → partner iframe with success/cancel
 *                                  detection wired through useIframeInspector.
 *   3. No cart, pending items    → list of items in `pending_pay` status,
 *                                  one-tap Pay CTA per row.
 *   4. No cart, no pending items → minimal empty state (large translucent
 *                                  card-icon). Rare — the Pay tab only
 *                                  surfaces in the bottom nav when at least
 *                                  one item needs payment, so this state
 *                                  appears mainly during the brief gap
 *                                  between paying off the last item and
 *                                  the auto-hide of the tab.
 *
 * The legacy WG-mock-form surface (DEMO_ACTIVITY + submitMockCheckout)
 * was removed: every real flow now hands off to a partner iframe via the
 * review step, and the mock form was confusing visitors into thinking
 * we'd take their card details ourselves.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useInstance } from '../context/InstanceContext';
import { useChatSession } from '../context/ChatSessionContext';
import { useView } from '../context/ViewContext';
import { useCheckout } from '../context/CheckoutContext';
import { useItinerary } from '../context/ItineraryContext';
import { useParentBridge } from '../context/ParentBridgeContext';
import ChatView from './ChatView';
import {
  initiateBookingForSession,
  getBookingStatusForSession,
  reportLikelyBookedForSession,
  type TrackingSessionSnapshot,
} from '../api/checkoutApi';
import type { ItineraryItem } from '../context/ItineraryContext';
import { emit as emitTelemetry } from '../utils/telemetry';
import { useIframeInspector } from '../hooks/useIframeInspector';
import { httpClient } from '../client';

const formatCurrency = (
  cents: number,
  locale: string | undefined,
  code: string,
) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
  }).format(cents / 100);

const formatLongDate = (iso: string, locale: string) => {
  const d = new Date(iso + 'T12:00:00');
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
};

const formatShortDate = (iso: string, locale: string) => {
  try {
    const d = new Date(iso + 'T12:00:00');
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(d);
  } catch {
    return iso;
  }
};

interface ResolvedLineItem {
  name: string;
  image_url?: string;
  date_iso?: string;
  guest_type?: string;
  guest_count?: number;
  unit_price_cents?: number;
  currency: string;
}

function resolveLineItem(
  cart: ReturnType<typeof useCheckout>['cart'],
): ResolvedLineItem | null {
  if (!cart) return null;
  const firstGuest = cart.guests?.[0];
  return {
    name: cart.activity_name,
    image_url: cart.image_url,
    date_iso: cart.date,
    guest_type: firstGuest?.type,
    guest_count: firstGuest?.count,
    unit_price_cents: cart.unit_price_cents,
    currency: cart.currency_code || 'USD',
  };
}

const CheckoutView: React.FC = () => {
  const { t } = useTranslation();
  const { effectiveLocale } = useInstance();
  const { chatSessionId } = useChatSession();
  const { setView, chatEnabled } = useView();
  const { cart, clear } = useCheckout();
  const { items: itinItems, startCheckout, removeItem } = useItinerary();
  const { setCart } = useCheckout();
  const { layoutMode, layoutMax, requestLayout, hostViewport } =
    useParentBridge();

  // Desktop auto-expand on Pay-view open. The dedicated `prefill_checkout`
  // action already promotes the panel when the visitor taps Reserve on a
  // card, but every OTHER entry into Pay (header Pay tab, ItineraryView's
  // per-row Pay CTA, deep-link recovery, etc.) used to leave the panel in
  // native/compact size — the partner iframe + review form had to cram
  // into a 360px column. Centralise the promotion here so any path that
  // lands on the Pay surface gets the same layout, respecting the
  // partner's layoutMax cap (native partners stay native) and the
  // viewport gate so phones don't get unwanted promotion.
  const didAutoExpandRef = useRef(false);
  useEffect(() => {
    if (didAutoExpandRef.current) return;
    if (layoutMax === 'native') return;
    if (hostViewport?.isMobile) return;
    // hostViewport may not have arrived yet (iframe still resolving the
    // wg:viewport message). Fall back to window.innerWidth so the first-
    // open path on a fresh session still promotes immediately instead of
    // waiting for the next handshake round-trip.
    if (
      !hostViewport &&
      typeof window !== 'undefined' &&
      window.innerWidth < 720
    ) {
      return;
    }
    if (layoutMode !== 'native') return;
    didAutoExpandRef.current = true;
    requestLayout('expanded');
  }, [layoutMax, layoutMode, hostViewport, requestLayout]);

  const line = useMemo(() => resolveLineItem(cart), [cart]);

  // Used by both the review and embedded surfaces when the visitor backs
  // out of checkout. We deliberately do NOT collapse the panel here — on
  // desktop the panel auto-expanded on first open and the user expects to
  // stay in that layout. The header toggle is the user's escape hatch
  // back to native if they want it.
  const onExitCheckout = useCallback(() => {
    setView('chat');
    clear();
  }, [setView, clear]);

  // Two-column shell — slim chat rail on the left so the agent stays
  // alive next to the partner booking iframe. Only kicks in when the
  // panel has been promoted to expanded/maximized; native panels keep
  // the existing single-column layout. When the instance has chat
  // disabled (feature_flags.chat === false) there is no rail to keep
  // alive, so we never split — checkout stays single-column.
  const showSplit =
    chatEnabled && (layoutMode === 'expanded' || layoutMode === 'maximized');

  // -------------------------------------------------------------------------
  // 1. WG-controlled review (auto-skips when trip profile is complete).
  // -------------------------------------------------------------------------
  // NOT gated on embedded_url. The review surface is what CALLS /book to
  // OBTAIN that URL, so requiring one up front deadlocked every item added
  // straight from an explore card or chat card: the item has no URL yet, so
  // this branch and the iframe branch below both fell through, and the Pay
  // tap silently re-rendered the same pending list. An item that carries a
  // provider is bookable; the surface fetches the rest.
  if (cart?.requires_review && (cart.embedded_url || cart.provider)) {
    return (
      <CheckoutSplitShell active={showSplit}>
        <CheckoutReviewSurface
          sessionId={chatSessionId || undefined}
          onCancel={onExitCheckout}
          onBackLabel={t('checkout.back_to_chat')}
        />
      </CheckoutSplitShell>
    );
  }

  // -------------------------------------------------------------------------
  // 2. Partner iframe with success/cancel detection.
  // -------------------------------------------------------------------------
  if (cart?.embedded_url && line) {
    return (
      <CheckoutSplitShell active={showSplit}>
        <EmbeddedCheckoutSurface
          cart={cart}
          line={line}
          effectiveLocale={effectiveLocale}
          sessionId={chatSessionId || undefined}
          onBack={onExitCheckout}
          backLabel={t('checkout.back_to_chat')}
          title={t('checkout.partner_booking_page')}
        />
      </CheckoutSplitShell>
    );
  }

  // -------------------------------------------------------------------------
  // 3. Pending-pay list — itinerary items the visitor reserved but hasn't
  //    paid yet. Each row carries a single big "Pay" CTA; tap routes
  //    through the review surface (which auto-skips when the profile is
  //    complete) and onto the partner iframe.
  // -------------------------------------------------------------------------
  const pending = itinItems.filter(it => it.status === 'pending_pay');

  const onPay = (item: ItineraryItem) => {
    const newCart = startCheckout(item.id);
    if (!newCart) return;
    setCart(newCart);
    emitTelemetry('pay_tab_pay_clicked', {
      sessionId: chatSessionId || undefined,
      props: {
        item_id: item.id,
        provider: item.provider,
        activity_id: item.activity_id,
        booking_method: item.booking_method,
      },
    });
    // CheckoutView re-renders on next tick because cart changed; the
    // requires_review branch above takes over.
  };

  if (pending.length > 0) {
    return (
      <div className="wg-pay">
        <header className="wg-pay__header">
          <h2 className="wg-pay__title">{t('pay.title')}</h2>
          <p className="wg-pay__subtitle">
            {t('pay.subtitle', { count: pending.length })}
          </p>
        </header>
        <ul className="wg-pay__list" role="list">
          {pending.map(item => (
            <PendingRow
              key={item.id}
              item={item}
              locale={effectiveLocale}
              onPay={() => onPay(item)}
              onRemove={() => removeItem(item.id)}
            />
          ))}
        </ul>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // 4. Empty state — the Pay tab only appears in the nav when there's
  //    something to pay, so this is a brief flash between "paid the last
  //    thing" and the tab auto-hiding. Match the Trip empty pattern: just
  //    a large translucent icon, no copy.
  // -------------------------------------------------------------------------
  return (
    <div
      className="wg-pay wg-pay--empty"
      role="button"
      tabIndex={0}
      aria-label={t('nav.pay')}
      onClick={() => setView('chat')}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setView('chat');
        }
      }}
    >
      <div className="wg-pay__empty-icon" aria-hidden>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="6" width="20" height="13" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
          <line x1="6" y1="15" x2="10" y2="15" />
        </svg>
      </div>
    </div>
  );
};

/** One pending-pay row — image + meta + a single big Pay CTA. The CTA
 * shows the total when known so the visitor doesn't have to mentally
 * multiply price × party. */
const PendingRow: React.FC<{
  item: ItineraryItem;
  locale: string;
  onPay: () => void;
  onRemove: () => void;
}> = ({ item, locale, onPay, onRemove }) => {
  const { t } = useTranslation();
  const { tripProfile } = useItinerary();
  // Prefer the item's confirmed party (set by the review-surface submit).
  // When the item was added straight from a card and never went through
  // review, fall back to the trip profile's party. As a last resort,
  // assume 2 adults — the same default the brain uses — so the Pay CTA
  // shows a meaningful total instead of the per-person price.
  const itemTotal = (item.guests || []).reduce((s, g) => s + (g.count || 0), 0);
  const profileTotal =
    (tripProfile.party?.adults || 0) + (tripProfile.party?.children || 0);
  const totalCount =
    itemTotal > 0 ? itemTotal : profileTotal > 0 ? profileTotal : 2;
  const currency = item.currency_code || 'USD';
  const totalCents =
    typeof item.unit_price_cents === 'number'
      ? item.unit_price_cents * totalCount
      : undefined;
  const total =
    typeof totalCents === 'number'
      ? formatCurrency(totalCents, locale, currency)
      : null;
  return (
    <li className="wg-pay__row">
      {item.image_url ? (
        <img
          className="wg-pay__row-image"
          src={item.image_url}
          alt=""
          loading="lazy"
        />
      ) : (
        <div
          className="wg-pay__row-image wg-pay__row-image--placeholder"
          aria-hidden
        />
      )}
      <div className="wg-pay__row-body">
        <h3 className="wg-pay__row-title">{item.title}</h3>
        <div className="wg-pay__row-meta">
          {item.date && <span>{formatShortDate(item.date, locale)}</span>}
          {totalCount > 0 && (
            <span>{t('pay.party_summary', { count: totalCount })}</span>
          )}
        </div>
        <div className="wg-pay__row-actions">
          <button
            type="button"
            className="wg-pay__row-cta"
            onClick={onPay}
            aria-label={
              total ? t('pay.pay_amount', { amount: total }) : t('pay.pay_now')
            }
          >
            {total ? t('pay.pay_amount', { amount: total }) : t('pay.pay_now')}
          </button>
          <button
            type="button"
            className="wg-pay__row-remove"
            onClick={onRemove}
          >
            {t('common.remove')}
          </button>
        </div>
      </div>
    </li>
  );
};

/**
 * Two-column shell — drops a slim ChatView rail on the left when the panel
 * has been promoted to the expanded/maximized layout, so the agent stays
 * reachable while the visitor fills the partner booking form. When `active`
 * is false, renders children directly (no wrapper, no rail) so native-panel
 * layouts behave exactly as before.
 */
const CheckoutSplitShell: React.FC<{
  active: boolean;
  children: React.ReactNode;
}> = ({ active, children }) => {
  const { chatEnabled } = useView();
  // The split exists solely to keep the chat rail alive beside checkout.
  // If chat is disabled for this instance, there is nothing to keep alive —
  // never mount ChatView, regardless of what the caller passes for `active`.
  if (!active || !chatEnabled) return <>{children}</>;
  return (
    <div className="wg-checkout-split">
      <aside
        className="wg-checkout-split__chat"
        aria-label="Chat with assistant"
      >
        <ChatView />
      </aside>
      <section className="wg-checkout-split__main">{children}</section>
    </div>
  );
};

/**
 * Embedded-URL surface — partner-iframe booking flow with the iframe
 * inspector wired so we know when success / cancel / abandonment happen.
 * Sub-component so we can call useIframeInspector at the top level
 * (one-iframe-per-mount).
 */
const EmbeddedCheckoutSurface: React.FC<{
  cart: NonNullable<ReturnType<typeof useCheckout>['cart']>;
  line: ResolvedLineItem;
  effectiveLocale: string;
  sessionId?: string;
  onBack: () => void;
  backLabel: string;
  title: string;
}> = ({ cart, line, effectiveLocale, sessionId, onBack, backLabel, title }) => {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [terminalState, setTerminalState] = useState<
    null | { kind: 'success'; code?: string } | { kind: 'cancel' }
  >(null);
  // Some partners refuse to be framed (X-Frame-Options / CSP frame-ancestors).
  // Viator is the standing example: iframes need their "Full + Booking Access"
  // tier, so on the affiliate tier the backend returns booking_type
  // 'redirect' and the frame renders blank forever. We still ATTEMPT the
  // iframe — plenty of partners (PonoRez) do frame fine, and we'd rather not
  // kick anyone out to a tab on a guess — then fall back to a hand-off card
  // once we can see nothing rendered.
  const [frameBlocked, setFrameBlocked] = useState(false);

  const onSuccess = useCallback(
    (info: { source: 'url' | 'message'; confirmation_code?: string }) => {
      setTerminalState({ kind: 'success', code: info.confirmation_code });
    },
    [],
  );
  const onCancel = useCallback(() => {
    setTerminalState({ kind: 'cancel' });
  }, []);

  useIframeInspector({
    iframeRef,
    active: !!cart.embedded_url && !frameBlocked,
    patterns: cart.detection_patterns,
    itemId: cart.item_id,
    sessionId,
    onSuccess,
    onCancel,
  });

  // Probe whether anything actually rendered. A frame that loaded a real
  // cross-origin document throws SecurityError on location access; a frame
  // the partner REFUSED is left parked on about:blank, which reads back
  // cleanly. That asymmetry is the only signal a cross-origin iframe gives
  // us — there is no error event for an X-Frame-Options refusal, and the
  // load event fires either way.
  const probeFrame = useCallback(() => {
    const el = iframeRef.current;
    if (!el) return;
    let blocked = false;
    try {
      const href = el.contentWindow?.location?.href;
      blocked = !href || href === 'about:blank';
    } catch {
      blocked = false; // SecurityError ⇒ a real partner document is loaded.
    }
    if (blocked) {
      setFrameBlocked(true);
      emitTelemetry('checkout_iframe_blocked', {
        sessionId,
        props: {
          item_id: cart.item_id,
          provider: cart.provider,
          booking_method: cart.booking_method,
        },
      });
    }
  }, [sessionId, cart.item_id, cart.provider, cart.booking_method]);

  // Deadline probe — covers the case where `load` never fires at all (some
  // browsers suppress it on refusal) so we can't rely on onLoad alone.
  useEffect(() => {
    if (frameBlocked) return;
    const id = window.setTimeout(probeFrame, 5000);
    return () => window.clearTimeout(id);
  }, [probeFrame, frameBlocked]);

  const providerLabel = cart.provider
    ? cart.provider.charAt(0).toUpperCase() + cart.provider.slice(1)
    : t('checkout.partner_booking_page');

  // ── Hand-off bracket ────────────────────────────────────────────────
  // Once the visitor leaves for the partner tab we can observe nothing on
  // their side — but the server can: /book created a tracking_session, and
  // the provider booking sync reconciles it. So instead of ending at "we
  // opened a tab, good luck", the panel holds a waiting surface and polls
  // that session, flipping to confirmed in our own chrome when the sync
  // lands. The visitor pays elsewhere; the flow still ENDS here.
  const [handedOff, setHandedOff] = useState(false);
  const [selfReported, setSelfReported] = useState(false);
  const trackingSessionId = cart.tracking_session_id;

  const applyStatus = useCallback((snap: TrackingSessionSnapshot | null) => {
    if (!snap) return;
    if (snap.status === 'confirmed') {
      setTerminalState({
        kind: 'success',
        code:
          snap.confirmation_code ||
          snap.possible_confirmation_code ||
          undefined,
      });
    } else if (snap.status === 'likely_booked') {
      setSelfReported(true);
    }
  }, []);

  const pollStatus = useCallback(async () => {
    if (!sessionId || !trackingSessionId) return;
    try {
      applyStatus(
        await getBookingStatusForSession(
          sessionId,
          trackingSessionId,
          httpClient,
        ),
      );
    } catch {
      // Transient — the next tick retries. A failed poll must never
      // surface as a booking error; the booking itself is unaffected.
    }
  }, [sessionId, trackingSessionId, applyStatus]);

  useEffect(() => {
    if (!handedOff || terminalState) return;
    if (!sessionId || !trackingSessionId) return;
    const id = window.setInterval(pollStatus, 8000);
    // Returning to the tab is the strongest signal there is that something
    // happened on the partner side — check immediately rather than waiting
    // out the interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') pollStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    pollStatus();
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [handedOff, terminalState, sessionId, trackingSessionId, pollStatus]);

  const onSelfReport = useCallback(async () => {
    if (!sessionId || !trackingSessionId) return;
    setSelfReported(true);
    emitTelemetry('checkout_handoff_self_reported', {
      sessionId,
      props: { item_id: cart.item_id, provider: cart.provider },
    });
    try {
      applyStatus(
        await reportLikelyBookedForSession(
          sessionId,
          trackingSessionId,
          httpClient,
        ),
      );
    } catch {
      // Already reflected optimistically in the UI; the sync reconciles.
    }
  }, [sessionId, trackingSessionId, cart.item_id, cart.provider, applyStatus]);

  return (
    <div className="wg-checkout wg-checkout--embedded">
      <NavHeader title={line.name} onBack={onBack} backLabel={backLabel}>
        {line.date_iso && (
          <p className="wg-navhdr__subtitle">
            {formatLongDate(line.date_iso, effectiveLocale)}
            {typeof line.guest_count === 'number' &&
              ` · ${line.guest_count} ${line.guest_type ?? ''}`.trim()}
          </p>
        )}
      </NavHeader>

      {terminalState?.kind === 'success' && (
        <div className="wg-checkout__embedded-banner wg-checkout__embedded-banner--success">
          <span aria-hidden>✓</span>
          <span>
            {t('embedded_checkout.booking_confirmed')}
            {terminalState.code ? ` · ${terminalState.code}` : ''}
          </span>
          <button
            type="button"
            className="wg-checkout__btn wg-checkout__btn--secondary"
            onClick={onBack}
          >
            {t('common.done')}
          </button>
        </div>
      )}
      {terminalState?.kind === 'cancel' && (
        <div className="wg-checkout__embedded-banner wg-checkout__embedded-banner--cancel">
          <span>{t('embedded_checkout.booking_cancelled')}</span>
          <button
            type="button"
            className="wg-checkout__btn wg-checkout__btn--secondary"
            onClick={onBack}
          >
            {t('common.back')}
          </button>
        </div>
      )}

      {frameBlocked ? (
        <div className="wg-checkout__handoff">
          <h3 className="wg-checkout__handoff-title">
            {t('embedded_checkout.blocked_title', { provider: providerLabel })}
          </h3>
          <p className="wg-checkout__handoff-body">
            {t('embedded_checkout.blocked_body', { provider: providerLabel })}
          </p>
          <a
            className="wg-checkout__btn wg-checkout__btn--primary wg-checkout__handoff-cta"
            href={cart.embedded_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              setHandedOff(true);
              emitTelemetry('checkout_handoff_clicked', {
                sessionId,
                props: {
                  item_id: cart.item_id,
                  provider: cart.provider,
                },
              });
            }}
          >
            {handedOff
              ? t('embedded_checkout.handoff_reopen', {
                  provider: providerLabel,
                })
              : t('embedded_checkout.blocked_cta', { provider: providerLabel })}
          </a>

          {/* Waiting bracket — only after they've actually left, and only
              when there's a tracking session to watch. */}
          {handedOff && trackingSessionId && (
            <div className="wg-checkout__handoff-wait" aria-live="polite">
              <span className="wg-review__spinner" aria-hidden />
              <p className="wg-checkout__handoff-waittext">
                {selfReported
                  ? t('embedded_checkout.handoff_confirming')
                  : t('embedded_checkout.handoff_waiting', {
                      provider: providerLabel,
                    })}
              </p>
              {!selfReported && (
                <button
                  type="button"
                  className="wg-checkout__btn wg-checkout__btn--quiet"
                  onClick={onSelfReport}
                >
                  {t('embedded_checkout.handoff_done')}
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          className="wg-checkout__embedded-frame"
          src={cart.embedded_url}
          title={title}
          onLoad={probeFrame}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      )}
    </div>
  );
};

/**
 * CheckoutReviewSurface — WG-controlled confirmation step that runs
 * before the partner iframe loads. Visitor edits date + adult/child
 * counts (and optional name/email prefill) in our chrome; submit
 * re-calls /catalog/v1/checkout-build via the chat-router proxy with
 * the new values, swaps `cart.embedded_url`, and clears
 * `requires_review` so the next render mounts the iframe.
 *
 * Inputs are intentionally minimal — date + party size + optional
 * contact. Anything else (special requests, transport, etc.) stays on
 * the provider page where the visitor expects it.
 */
const CheckoutReviewSurface: React.FC<{
  sessionId?: string;
  onCancel: () => void;
  onBackLabel: string;
}> = ({ sessionId, onCancel, onBackLabel }) => {
  const { t } = useTranslation();
  const { cart, setCart, prefillContact, setPrefillContact } = useCheckout();
  const {
    tripProfile,
    updateTripProfile,
    updateItem: updateItineraryItem,
  } = useItinerary();

  // Layered prefill: card → cart → trip profile → fallback.
  // Card-emitted values win when present (they're activity-specific),
  // then the visitor's previous trip profile fills the rest. This is the
  // "capture once, reuse" backbone — second booking shouldn't re-ask.
  const initialDate = cart?.date || tripProfile.default_date || '';
  const initialAdults =
    cart?.guests?.find(g => /adult|trad/i.test(g.type))?.count ??
    cart?.guests?.[0]?.count ??
    tripProfile.party?.adults ??
    2;
  const initialKids =
    cart?.guests?.find(g => /child|kid/i.test(g.type))?.count ??
    tripProfile.party?.children ??
    0;

  const [date, setDate] = useState(initialDate);
  const [adults, setAdults] = useState<number>(initialAdults);
  const [kids, setKids] = useState<number>(initialKids);
  const [firstName, setFirstName] = useState(
    tripProfile.contact?.name_first ||
      prefillContact?.name?.split(' ')[0] ||
      '',
  );
  const [lastName, setLastName] = useState(
    tripProfile.contact?.name_last ||
      prefillContact?.name?.split(' ').slice(1).join(' ') ||
      '',
  );
  const [email, setEmail] = useState(
    tripProfile.contact?.email || prefillContact?.email || '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "Client unawares" mode: if the visitor's trip profile already has
  // name/email/party/date, we skip the form and auto-rebuild the URL with
  // those values so they land directly on the partner iframe with their
  // info pre-populated. A subtle "Change details" link below the spinner
  // gives them an escape hatch if anything's wrong.
  const profileComplete =
    !!tripProfile.contact?.name_first &&
    !!tripProfile.contact?.email &&
    !!tripProfile.party &&
    !!initialDate;
  const [showForm, setShowForm] = useState(!profileComplete);

  // Resolve adult/child tier ids from cart.guests so the rebuild call
  // sends `tier_id` as well as count. Brain's _emit_activity_cards
  // packs guests with provider tier ids when known; if missing (rare
  // for partners we haven't enriched), tier_id defaults to undefined
  // and the backend's auto-resolve picks them up.
  const adultTierId = useMemo(
    () =>
      (
        cart?.guests as
          | { type: string; count: number; tier_id?: string | number }[]
          | undefined
      )?.find(g => /adult|trad/i.test(g.type))?.tier_id,
    [cart?.guests],
  );
  const kidTierId = useMemo(
    () =>
      (
        cart?.guests as
          | { type: string; count: number; tier_id?: string | number }[]
          | undefined
      )?.find(g => /child|kid/i.test(g.type))?.tier_id,
    [cart?.guests],
  );

  if (!cart) return null;

  const totalGuests = adults + kids;
  const canSubmit =
    !!date && totalGuests >= 1 && !!sessionId && !!cart.provider && !submitting;

  // Fire the auto-skip exactly once: when the surface mounts with a
  // complete profile, kick off the rebuild immediately. Don't re-fire
  // on prop changes, otherwise toggling "Change details" then back
  // would loop.
  useEffect(() => {
    if (showForm) return;
    if (submitting) return;
    if (!canSubmit) {
      // Profile thought it was complete but a guard failed (no
      // sessionId yet, no provider, etc.). Surface the form so the
      // visitor isn't stuck on a spinner.
      setShowForm(true);
      return;
    }
    onConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  const onConfirm = async () => {
    if (!sessionId || !cart.provider) return;
    setError(null);
    setSubmitting(true);

    const guests: { tier_id: string | number; count: number }[] = [];
    if (adults > 0 && adultTierId !== undefined) {
      guests.push({ tier_id: adultTierId, count: adults });
    }
    if (kids > 0 && kidTierId !== undefined) {
      guests.push({ tier_id: kidTierId, count: kids });
    }

    emitTelemetry('checkout_review_submit', {
      sessionId,
      props: {
        item_id: cart.item_id,
        provider: cart.provider,
        activity_id: cart.activity_id,
        date,
        adults,
        kids,
        has_contact: !!(firstName || email),
      },
    });

    try {
      const productId = cart.activity_id;
      if (!productId) throw new Error('Missing activity id');
      // Compose the provider-slug item id the proxy expects in the URL.
      // /experiences/v1/{id}/book resolves this server-side to a catalog UUID.
      // activity_id may arrive as a BARE product code (brain tool calls) OR as
      // the already-composite `<provider>_<code>` (explore list item ids). Do
      // NOT double-prefix one that already carries the provider — that yields
      // e.g. `viator_viator_409905P1`, which resolves to a non-existent product
      // and produces an empty checkout.
      const itemId =
        cart.provider && !productId.startsWith(`${cart.provider}_`)
          ? `${cart.provider}_${productId}`
          : productId;
      const built = await initiateBookingForSession(
        sessionId,
        itemId,
        {
          provider_id: cart.provider,
          product_id: productId,
          date,
          guests,
          contact:
            firstName || lastName || email
              ? {
                  first_name: firstName || undefined,
                  last_name: lastName || undefined,
                  email: email || undefined,
                }
              : undefined,
        },
        httpClient,
      );

      // Persist contact so the iframe + later flows have it.
      if (firstName || lastName || email) {
        setPrefillContact({
          name: `${firstName} ${lastName}`.trim() || undefined,
          email: email || undefined,
        });
      }

      // Capture-once: fold every entry into the trip profile so the
      // NEXT booking's review form lands pre-filled. Backend stores in
      // itinerary.metadata.trip_profile, so it survives reload + cross
      // device for authed users.
      updateTripProfile({
        contact: {
          name_first: firstName || undefined,
          name_last: lastName || undefined,
          email: email || undefined,
        },
        party: { adults, children: kids },
        default_date: date || tripProfile.default_date,
      });

      // Write the confirmed party + date back to the underlying
      // itinerary item so the Pay tab + Trip view show the right total
      // (unit_price × actual party). Without this, Pay reads stale
      // guests from the original brain-emitted item (often count=1)
      // and renders "Pay $230" for a 2-adult booking.
      if (cart.item_id) {
        const itemGuests: ItineraryItem['guests'] = [];
        if (adults > 0) {
          itemGuests.push({
            type: 'Adult',
            count: adults,
            tier_id: adultTierId,
          });
        }
        if (kids > 0) {
          itemGuests.push({
            type: 'Child',
            count: kids,
            tier_id: kidTierId,
          });
        }
        updateItineraryItem(cart.item_id, {
          date: date || undefined,
          guests: itemGuests.length > 0 ? itemGuests : undefined,
        });
      }

      // Update cart with the new URL + clear the review flag so the next
      // render mounts the EmbeddedCheckoutSurface.
      setCart({
        ...cart,
        date,
        guests:
          guests.length > 0
            ? guests.map((g, i) => ({
                type: i === 0 && adults > 0 ? 'Adult' : 'Child',
                count: g.count,
                tier_id: g.tier_id,
              }))
            : cart.guests,
        embedded_url: built.url,
        detection_patterns:
          (built.detection_patterns as
            | typeof cart.detection_patterns
            | undefined) || cart.detection_patterns,
        booking_method: built.booking_type || cart.booking_method,
        tracking_session_id:
          built.tracking_session_id || cart.tracking_session_id,
        requires_review: false,
      });
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: string } }; message?: string })
          .response?.data?.detail ||
        (e as Error).message ||
        t('checkout.processing');
      setError(msg);
      emitTelemetry('checkout_review_error', {
        sessionId,
        props: { item_id: cart.item_id, message: msg },
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!showForm) {
    // Auto-skip surface — visitor's profile is complete, we're calling
    // checkout-build in the background and will swap in the iframe as
    // soon as it returns. Keep this minimal so it reads as a hand-off,
    // not a step.
    return (
      <div className="wg-checkout wg-review wg-review--auto">
        <NavHeader
          title={cart.activity_name}
          onBack={onCancel}
          backLabel={onBackLabel}
        />
        {cart.image_url && (
          <img className="wg-review__image" src={cart.image_url} alt="" />
        )}
        <div className="wg-review__autoflow">
          <span className="wg-review__spinner" aria-hidden />
          <p className="wg-review__autotext">
            {t('checkout.auto_reserving', { name: cart.activity_name })}
          </p>
          <button
            type="button"
            className="wg-review__autoedit"
            onClick={() => setShowForm(true)}
          >
            {t('checkout.change_details')}
          </button>
        </div>
        {error && (
          <div className="wg-checkout__error" role="alert">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="wg-checkout wg-review">
      <NavHeader
        title={cart.activity_name}
        onBack={onCancel}
        backLabel={onBackLabel}
      />

      {cart.image_url && (
        <img className="wg-review__image" src={cart.image_url} alt="" />
      )}

      <section className="wg-review__form">
        <label className="wg-review__field">
          <span className="wg-review__label">{t('checkout.date_label')}</span>
          <input
            className="wg-checkout__input"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            min={new Date().toISOString().slice(0, 10)}
            disabled={submitting}
          />
        </label>

        <div className="wg-review__row">
          <Stepper
            label={t('checkout.adults_label')}
            value={adults}
            min={0}
            max={20}
            onChange={setAdults}
            disabled={submitting}
          />
          <Stepper
            label={t('checkout.children_label')}
            value={kids}
            min={0}
            max={20}
            onChange={setKids}
            disabled={submitting}
          />
        </div>

        <div className="wg-review__contact">
          <div className="wg-review__row">
            <label className="wg-review__field">
              <span className="wg-review__label">
                {t('checkout.first_name_label')}
              </span>
              <input
                className="wg-checkout__input"
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                autoComplete="given-name"
                disabled={submitting}
              />
            </label>
            <label className="wg-review__field">
              <span className="wg-review__label">
                {t('checkout.last_name_label')}
              </span>
              <input
                className="wg-checkout__input"
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                autoComplete="family-name"
                disabled={submitting}
              />
            </label>
          </div>
          <label className="wg-review__field">
            <span className="wg-review__label">{t('checkout.email')}</span>
            <input
              className="wg-checkout__input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              disabled={submitting}
            />
          </label>
        </div>
      </section>

      {(() => {
        // Live total — recomputes as steppers move so the visitor sees
        // the cost shift before they continue. Uses the same currency
        // the cart was emitted with; falls back to USD when unset.
        if (typeof cart.unit_price_cents !== 'number') return null;
        const currency = cart.currency_code || 'USD';
        // Show real cents — when the unit price is e.g. 230.37, we don't
        // want "$230 pp" with "$461" total because that's both wrong and
        // weird. Locale-default fraction digits for the currency.
        const fmt = (cents: number) =>
          formatCurrency(cents, undefined, currency);
        const total = cart.unit_price_cents * totalGuests;
        return (
          <div className="wg-review__total" aria-live="polite">
            <span className="wg-review__total-label">
              {t('checkout.total')}
            </span>
            <span className="wg-review__total-amount">{fmt(total)}</span>
            <span className="wg-review__total-detail">
              {fmt(cart.unit_price_cents)} pp · {totalGuests}{' '}
              {totalGuests === 1 ? 'guest' : 'guests'}
            </span>
          </div>
        );
      })()}

      {error && (
        <div className="wg-checkout__error" role="alert">
          {error}
        </div>
      )}

      <div className="wg-checkout__actions">
        <button
          type="button"
          className="wg-checkout__btn wg-checkout__btn--quiet"
          onClick={onCancel}
          disabled={submitting}
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="wg-checkout__btn wg-checkout__btn--primary"
          onClick={onConfirm}
          disabled={!canSubmit}
        >
          {submitting
            ? t('checkout.processing')
            : t('checkout.continue_to_pay')}
        </button>
      </div>
    </div>
  );
};

/**
 * Mobile-app-pattern header: a 44px row with a chevron-left back button on
 * the left and the title (truncated mid-string when needed) right of it.
 * Replaces the old "title left, big text-button right" layout that
 * squished the back affordance whenever an activity title was long. The
 * chevron is theme-tonal and tappable; `backLabel` becomes the aria-label
 * so screenreaders still announce "Back to chat" or whatever the caller
 * passes.
 */
const NavHeader: React.FC<{
  title: string;
  onBack: () => void;
  backLabel: string;
  children?: React.ReactNode;
}> = ({ title, onBack, backLabel, children }) => (
  <header className="wg-navhdr">
    <button
      type="button"
      className="wg-navhdr__back"
      onClick={onBack}
      aria-label={backLabel}
      title={backLabel}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
    <div className="wg-navhdr__titles">
      <h2 className="wg-navhdr__title" title={title}>
        {title}
      </h2>
      {children}
    </div>
  </header>
);

const Stepper: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}> = ({ label, value, min, max, onChange, disabled }) => (
  <div className="wg-stepper">
    <span className="wg-stepper__label">{label}</span>
    <div className="wg-stepper__row">
      <button
        type="button"
        className="wg-stepper__btn"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={disabled || value <= min}
        aria-label="−"
      >
        −
      </button>
      <span className="wg-stepper__value">{value}</span>
      <button
        type="button"
        className="wg-stepper__btn"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={disabled || value >= max}
        aria-label="+"
      >
        +
      </button>
    </div>
  </div>
);

export default CheckoutView;
